import {
  BadgeDollarSign,
  ChartNoAxesCombined,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleX,
  ClipboardList,
  Clock3,
  Handshake,
  Mail,
  MapPin,
  NotebookPen,
  ShieldAlert,
  UserRound,
} from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { consultationsService } from '../services/api';
import './css/MeetingStatistics.css';
import { formatDate } from './utils/Common';
import {
  formatCurrency,
  getRejectionLabel
} from './utils/MeetingStatistics';


const MeetingStatistics = () => {
  const { hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [employeeConsultations, setEmployeeConsultations] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportTab, setReportTab] = useState('active');
  const [expandedConsultation, setExpandedConsultation] = useState(null);

  const canViewReports = hasPermission('meetings:view_reports');

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await consultationsService.getStatistics();
      setStats(data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Nie udało się załadować statystyk');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeClick = async (employeeId) => {
    if (!canViewReports) return;
    if (expandedEmployee === employeeId) {
      setExpandedEmployee(null);
      setEmployeeConsultations([]);
      setExpandedConsultation(null);
      return;
    }
    try {
      setLoadingReport(true);
      setExpandedEmployee(employeeId);
      setReportTab('active');
      setExpandedConsultation(null);
      const data = await consultationsService.getEmployeeConsultations(employeeId);
      setEmployeeConsultations(data.consultations || []);
    } catch (err) {
      console.error('Error fetching employee reports:', err);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleConsultationClick = (e, consultationId) => {
    e.stopPropagation();
    setExpandedConsultation(expandedConsultation === consultationId ? null : consultationId);
  };

  const renderBoolIcon = (value, compact = false) => {
    const Icon = value === true ? CircleCheck : value === false ? CircleX : Clock3;
    const label = value === true ? 'Tak' : value === false ? 'Nie' : 'Oczekujące';
    const statusClass = value === true ? 'yes' : value === false ? 'no' : 'pending';

    return (
      <span className={`bool-status bool-status-${statusClass}`} title={compact ? label : undefined}>
        <Icon aria-hidden="true" />
        {!compact && label}
      </span>
    );
  };

  const getStatusLabel = (status) => {
    const labels = { not_started: 'Nierozpoczęte', in_progress: 'W trakcie', completed: 'Zakończone' };
    return labels[status] || status;
  };

  const DetailRow = ({ label, value, className = '' }) => (
    <div className={`detail-row ${className}`}>
      <span className="detail-label">{label}:</span>
      {typeof value === 'string' ? <span className="detail-value">{value}</span> : value}
    </div>
  );
  
  if (loading) {
    return (
      <div className="statistics-page">
        <div className="page-header">
          <h2 className="sales-module-title"><ChartNoAxesCombined aria-hidden="true" /> Statystyki spotkań</h2>
        </div>
        <div className="loading-spinner">Ładowanie statystyk...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="statistics-page">
        <div className="page-header">
          <h2 className="sales-module-title"><ChartNoAxesCombined aria-hidden="true" /> Statystyki spotkań</h2>
        </div>
        <div className="error-message">
          {error}
          <button onClick={fetchStatistics}>Ponów</button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="statistics-page">
      <div className="page-header">
        <h2 className="sales-module-title"><ChartNoAxesCombined aria-hidden="true" /> Statystyki spotkań</h2>
        <p>Przeglądaj statystyki efektywności handlowców.</p>
      </div>

      {/* Summary Cards */}
      <div className="stats-summary">
        <div className="stat-card total">
          <div className="stat-icon"><Mail aria-hidden="true" /></div>
          <div className="stat-info">
            <div className="stat-value">{stats.total_requests}</div>
            <div className="stat-label">Łączna liczba leadów</div>
          </div>
        </div>
        <div className="stat-card assigned">
          <div className="stat-icon"><UserRound aria-hidden="true" /></div>
          <div className="stat-info">
            <div className="stat-value">{stats.total_assigned}</div>
            <div className="stat-label">Przypisanych</div>
          </div>
        </div>
        <div className="stat-card completed">
          <div className="stat-icon"><CircleCheck aria-hidden="true" /></div>
          <div className="stat-info">
            <div className="stat-value">{stats.total_completed}</div>
            <div className="stat-label">Zakończonych</div>
          </div>
        </div>
        <div className="stat-card successful">
          <div className="stat-icon"><Handshake aria-hidden="true" /></div>
          <div className="stat-info">
            <div className="stat-value">{stats.total_successful}</div>
            <div className="stat-label">Podpisanych umów</div>
          </div>
        </div>
        <div className="stat-card unsuccessful">
          <div className="stat-icon"><CircleX aria-hidden="true" /></div>
          <div className="stat-info">
            <div className="stat-value">{stats.total_unsuccessful}</div>
            <div className="stat-label">Nie podpisanych umów</div>
          </div>
        </div>
        <div className="stat-card spam">
          <div className="stat-icon"><ShieldAlert aria-hidden="true" /></div>
          <div className="stat-info">
            <div className="stat-value">{stats.total_spam}</div>
            <div className="stat-label">Spam</div>
          </div>
        </div>
        <div className="stat-card amount">
          <div className="stat-icon"><BadgeDollarSign aria-hidden="true" /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(stats.total_deal_amount)}</div>
            <div className="stat-label">Łączna wartość transakcji</div>
          </div>
        </div>
      </div>

      {/* Source Breakdown */}
      <div className="stats-section source-section">
        <h3><MapPin aria-hidden="true" /> Źródła</h3>
        <p className="section-subtitle">Skąd trafiają do Nas leady?</p>
        {(!stats.sources || stats.sources.length === 0) ? (
          <div className="empty-state">
            <p>Brak dostępnych danych o źródłach.</p>
          </div>
        ) : (
          <div className="source-grid">
            {stats.sources.map(src => {
              const pct = stats.total_requests > 0 ? Math.round((src.count / stats.total_requests) * 100) : 0;
              return (
                <div key={src.source} className="source-card">
                  <div className="source-header">
                    <span className="source-name">{src.source}</span>
                    <span className="source-count">{src.count}</span>
                  </div>
                  <div className="source-bar-bg">
                    <div className="source-bar-fill" style={{ width: `${pct}%` }}></div>
                  </div>
                  <div className="source-details">
                    <span className="source-pct">{pct}%</span>
                    <span className="source-breakdown">
                      <span className="source-success"><CircleCheck aria-hidden="true" /> {src.successful}</span>
                      <span className="source-fail"><CircleX aria-hidden="true" /> {src.unsuccessful}</span>
                      <span className="source-pending"><Clock3 aria-hidden="true" /> {src.in_progress}</span>
                      <span className="source-spam"><ShieldAlert aria-hidden="true" /> {src.spam}</span>
                    </span>
                  </div>
                  {src.total_deal_amount > 0 && (
                    <div className="source-amount"><BadgeDollarSign aria-hidden="true" /> {formatCurrency(src.total_deal_amount)}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Employee Table */}
      <div className="stats-section">
        <h3>Wydajność pracowników</h3>
        {stats.employees.length === 0 ? (
          <div className="empty-state">
            <p>Brak dostępnych danych o pracownikach.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="stats-table">
              <thead>
                <tr>
                  <th>Pracownik</th>
                  <th>Przypisane</th>
                  <th>W trakcie</th>
                  <th>Udane</th>
                  <th>Nieudane</th>
                  <th>Spam</th>
                  <th>Wskaźnik sukcesu</th>
                  <th>Łączna wartość transakcji</th>
                </tr>
              </thead>
              <tbody>
                {stats.employees.map(emp => {
                  const completed = emp.successful + emp.unsuccessful;
                  const successRate = completed > 0 ? Math.round((emp.successful / completed) * 100) : 0;
                  const isExpanded = expandedEmployee === emp.employee_id;
                  const activeItems = employeeConsultations.filter(c => !c.is_spam && c.order_status !== 'completed');
                  const successItems = employeeConsultations.filter(c => !c.is_spam && c.order_status === 'completed' && c.deal_signed === true);
                  const failItems = employeeConsultations.filter(c => !c.is_spam && c.order_status === 'completed' && c.deal_signed !== true);
                  const spamItems = employeeConsultations.filter(c => c.is_spam);
                  const reportItems = {
                    active: activeItems,
                    successful: successItems,
                    unsuccessful: failItems,
                    spam: spamItems,
                  };
                  return (
                    <Fragment key={emp.employee_id}>
                    <tr className={`${canViewReports ? 'clickable-row' : ''} ${isExpanded ? 'expanded-emp-row' : ''}`} onClick={() => handleEmployeeClick(emp.employee_id)}>
                      <td className="employee-name">
                        {canViewReports && (
                          <span className="expand-arrow">
                            {isExpanded
                              ? <ChevronDown aria-hidden="true" />
                              : <ChevronRight aria-hidden="true" />}
                          </span>
                        )}
                        {emp.employee_name}
                      </td>
                      <td className="center">{emp.total_assigned}</td>
                      <td className="center">{emp.in_progress}</td>
                      <td className="center success-cell">{emp.successful}</td>
                      <td className="center fail-cell">{emp.unsuccessful}</td>
                      <td className="center spam-cell">{emp.spam}</td>
                      <td className="center">
                        <div className="rate-bar-wrapper">
                          <div className="rate-bar" style={{ width: `${successRate}%` }}></div>
                          <span className="rate-label">{successRate}%</span>
                        </div>
                      </td>
                      <td className="amount-cell">{formatCurrency(emp.total_deal_amount)}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="employee-report-row">
                        <td colSpan={8}>
                          {loadingReport ? (
                            <div className="report-loading">Ładowanie raportu pracownika...</div>
                          ) : (
                            <div className="employee-report">
                              <div className="report-tabs">
                                <button className={`report-tab ${reportTab === 'active' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setReportTab('active'); }}>
                                  <ClipboardList aria-hidden="true" /> Aktywne ({activeItems.length})
                                </button>
                                <button className={`report-tab ${reportTab === 'successful' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setReportTab('successful'); }}>
                                  <CircleCheck aria-hidden="true" /> Udane ({successItems.length})
                                </button>
                                <button className={`report-tab ${reportTab === 'unsuccessful' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setReportTab('unsuccessful'); }}>
                                  <CircleX aria-hidden="true" /> Nieudane ({failItems.length})
                                </button>
                                <button className={`report-tab report-tab-spam ${reportTab === 'spam' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setReportTab('spam'); }}>
                                  <ShieldAlert aria-hidden="true" /> Spam ({spamItems.length})
                                </button>
                              </div>
                              {(() => {
                                const items = reportItems[reportTab] || [];
                                if (items.length === 0) return <div className="report-empty">Brak pozycji w tej kategorii.</div>;
                                return (
                                  <table className="report-table">
                                    <thead>
                                      <tr>
                                        <th>ID</th>
                                        <th>Data</th>
                                        <th>Imię i nazwisko</th>
                                        <th>Temat</th>
                                        <th>Kod pocztowy</th>
                                        <th>Miasto</th>
                                        <th>Kontakt</th>
                                        <th>Umowa podpisana</th>
                                        <th>Status</th>
                                        {reportTab === 'successful' && <th>Kwota transakcji</th>}
                                        {reportTab === 'unsuccessful' && <th>Powód odmowy</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {items.map(c => {
                                        const isConsultationExpanded = expandedConsultation === c.id;
                                        return (
                                          <Fragment key={c.id}>
                                            <tr 
                                              className={`consultation-row ${isConsultationExpanded ? 'expanded-consultation' : ''}`}
                                              onClick={(e) => handleConsultationClick(e, c.id)}
                                            >
                                              <td>
                                                <span className="expand-arrow">
                                                  {isConsultationExpanded
                                                    ? <ChevronDown aria-hidden="true" />
                                                    : <ChevronRight aria-hidden="true" />}
                                                </span>
                                                {c.id}
                                              </td>
                                              <td>{formatDate(c.created_at, 'pl-PL')}</td>
                                              <td>{c.first_name} {c.last_name}</td>
                                              <td>{c.subject}</td>
                                              <td>{c.postcode || '—'}</td>
                                              <td>{c.town || '—'}</td>
                                              <td className="center">{renderBoolIcon(c.contacted, true)}</td>
                                              <td className="center">{renderBoolIcon(c.deal_signed, true)}</td>
                                              <td className="center">
                                                <span className={`status-badge ${c.is_spam ? 'status-spam' : `status-${c.order_status}`}`}>
                                                  {c.is_spam ? 'Spam' : getStatusLabel(c.order_status)}
                                                </span>
                                              </td>
                                              {reportTab === 'successful' && <td className="amount-cell">{formatCurrency(c.deal_amount)}</td>}
                                              {reportTab === 'unsuccessful' && <td>{c.rejection_reason ? getRejectionLabel(c.rejection_reason) : '—'}</td>}
                                            </tr>
                                            {isConsultationExpanded && (
                                              <tr className="consultation-details-row">
                                                <td colSpan={reportTab === 'successful' || reportTab === 'unsuccessful' ? 10 : 9}>
                                                  <div className="consultation-details">
                                                    <h4><ClipboardList aria-hidden="true" /> Szczegóły zgłoszenia #{c.id}</h4>
                                                    <div className="details-grid">
                                                      <div className="detail-section">
                                                        <h5><UserRound aria-hidden="true" /> Informacje o kliencie</h5>
                                                        <DetailRow label="Imię i nazwisko" value={`${c.first_name} ${c.last_name}`} />
                                                        <DetailRow label="Email" value={c.email || '—'} />
                                                        <DetailRow label="Telefon" value={c.phone || '—'} />
                                                        <DetailRow label="Kod pocztowy" value={c.postcode || '—'} />
                                                        <DetailRow label="Miasto" value={c.town || '—'} />
                                                        <DetailRow label="Adres" value={c.address || '—'} />
                                                      </div>
                                                      <div className="detail-section">
                                                        <h5><NotebookPen aria-hidden="true" /> Szczegóły zgłoszenia</h5>
                                                        <DetailRow label="Temat" value={c.subject} />
                                                        <DetailRow label="Źródło" value={c.source || '—'} />
                                                        <DetailRow label="Data utworzenia" value={formatDate(c.created_at, 'pl-PL')} />
                                                        <DetailRow label="Data aktualizacji" value={c.updated_at ? formatDate(c.updated_at, 'pl-PL') : '—'} />
                                                        {c.message && (
                                                          <DetailRow 
                                                            label="Wiadomość" 
                                                            className="full-width"
                                                            value={<div className="detail-value message-text" dangerouslySetInnerHTML={{ __html: c.message }} />}
                                                          />
                                                        )}
                                                      </div>
                                                      <div className="detail-section">
                                                        <h5><ChartNoAxesCombined aria-hidden="true" /> Status i wynik</h5>
                                                        <DetailRow 
                                                          label="Status" 
                                                          value={<span className={`status-badge ${c.is_spam ? 'status-spam' : `status-${c.order_status}`}`}>{c.is_spam ? 'Spam' : getStatusLabel(c.order_status)}</span>}
                                                        />
                                                        <DetailRow label="Klient skontaktowany" value={renderBoolIcon(c.contacted)} />
                                                        <DetailRow label="Umowa podpisana" value={renderBoolIcon(c.deal_signed)} />
                                                        {c.deal_amount > 0 && (
                                                          <DetailRow 
                                                            label="Kwota transakcji" 
                                                            value={<span className="highlight-amount">{formatCurrency(c.deal_amount)}</span>}
                                                          />
                                                        )}
                                                        {c.rejection_reason && <DetailRow label="Powód odmowy" value={getRejectionLabel(c.rejection_reason)} />}
                                                        {c.notes && (
                                                          <DetailRow 
                                                            label="Notatki" 
                                                            className="full-width"
                                                            value={<div className="detail-value notes-text" dangerouslySetInnerHTML={{ __html: c.notes }} />}
                                                          />
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                );
                              })()}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td><strong>Razem</strong></td>
                  <td className="center"><strong>{stats.total_assigned}</strong></td>
                  <td className="center"><strong>{stats.employees.reduce((s, e) => s + e.in_progress, 0)}</strong></td>
                  <td className="center success-cell"><strong>{stats.total_successful}</strong></td>
                  <td className="center fail-cell"><strong>{stats.total_unsuccessful}</strong></td>
                  <td className="center spam-cell"><strong>{stats.total_spam}</strong></td>
                  <td className="center">
                    <strong>
                      {stats.total_completed > 0 
                        ? Math.round((stats.total_successful / stats.total_completed) * 100) 
                        : 0}%
                    </strong>
                  </td>
                  <td className="amount-cell"><strong>{formatCurrency(stats.total_deal_amount)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingStatistics;
